import { useIntl } from 'react-intl';
import { useEffect, useState } from 'react';
import classnames from 'classnames';

import { DELIVERY_STATUSES } from 'constants/Businesses';
import { getFinancialMetrics, getOrderStatistics } from 'services/business';
import { isUserAuthorized } from 'utils/helpers';
import { ACL_MATRIX } from 'common/permissions';

import BusinessHeader from '../components/BusinessHeader/BusinessHeader';
import FinancialMetrics from '../components/FinancialMetrics/FinancialMetrics';
import StatCard from '../components/StatCard/StatCard';
import { notify } from 'components/Notify/Notify';
import StatisticsPie from '../components/cards/StatisticsPie/StatisticsPie';
import FreshDeskTable from '../components/FreshDeskTable/FreshDeskTable';

import { ReactComponent as File } from 'assets/bosta-icons/File.svg';

import './DetailedAnalytics.less';

const DetailedAnalytics = ({
  businessData,
  setIsLoading,
  dateValue,
  setDateValue,
  ...props
}) => {
  const intl = useIntl();
  const [financialMetricsData, setFinancialMetricsData] = useState({});
  const [orderStatisticsData, setOrderStatisticsData] = useState({});

  useEffect(() => {
    if (!dateValue || !businessData?._id) {
      setOrderStatisticsData({});
      setFinancialMetricsData({});
      return;
    }

    const fetchBusinessAnalytics = async () => {
      setIsLoading(true);
      const queryParams = {
        businessId: businessData?._id,
        date: dateValue?.format('YYYY-MM-DD'),
        limit: 50,
        page: 1
      };
      try {
        const [{ data: financialData }, { data: orderData }] =
          await Promise.all([
            getFinancialMetrics({ ...queryParams }),
            getOrderStatistics({ ...queryParams })
          ]);

        const {
          orderAnalysisInfo: { orderStates, DSR, numberOfOrders },
          numberOfPickupLocations,
          topDeliveryLocationsInfo
        } = orderData;

        setOrderStatisticsData({
          orderAnalysisInfo: {
            analysis: orderStates,
            DSR,
            businessCount: numberOfOrders
          },
          topDeliveryLocationsInfo,
          numberOfPickupLocations
        });

        setFinancialMetricsData(financialData);
      } catch (error) {
        notify(error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchBusinessAnalytics();
  }, [dateValue, businessData]);

  return (
    <div className="br-detailed-analytics">
      <BusinessHeader
        businessData={businessData}
        dateValue={dateValue}
        setDateValue={setDateValue}
        setIsLoading={setIsLoading}
      />
      <div
        className={classnames('br-detailed-analytics__content', {
          'br-detailed-analytics__content-clear': !dateValue
        })}
      >
        <div className="br-detailed-analytics__content-top">
          <div className="br-detailed-analytics__content-top-left">
            <FinancialMetrics financialMetricsData={financialMetricsData} />
          </div>
          <div className="br-detailed-analytics__content-top-right">
            <StatCard
              title={intl.formatMessage({
                id: 'merchant_analysis.order_statistics'
              })}
              titleIcon={
                <File className="br-overall-analytics__card-header-icon" />
              }
              className="br-statistics-pie"
            >
              <StatisticsPie
                rawData={orderStatisticsData?.orderAnalysisInfo?.analysis}
                referenceData={DELIVERY_STATUSES}
                businessCount={
                  orderStatisticsData?.orderAnalysisInfo?.businessCount
                }
                locationsInfo={orderStatisticsData?.topDeliveryLocationsInfo}
                pickupLocationsCount={
                  orderStatisticsData?.numberOfPickupLocations
                }
                DSR={orderStatisticsData?.orderAnalysisInfo?.DSR}
                numberOfPickupLocations={
                  orderStatisticsData?.numberOfPickupLocations
                }
                isBusinessAnalysis
              />
            </StatCard>
          </div>
        </div>

        {isUserAuthorized([], [ACL_MATRIX.BUSINESS_TICKETS_GET]) && (
          <div className="br-detailed-analytics__content-bottom">
            <FreshDeskTable businessId={businessData?._id} date={dateValue} />
          </div>
        )}
      </div>
    </div>
  );
};

export default DetailedAnalytics;
